import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Play, Heart } from "lucide-react";
import { KidAvatar } from "./Brand";
import { catById, timeAgo } from "../lib/constants";
import { thumbUrl, streamUrl, KID_KEY } from "../lib/api";

export const Thumb = ({ id, title, token = localStorage.getItem(KID_KEY), className = "" }) => {
  const [ok, setOk] = useState(false);
  return (
    <img
      data-testid={`thumb-${id}`}
      src={thumbUrl(id, token)}
      alt={title}
      loading="lazy"
      decoding="async"
      onLoad={() => setOk(true)}
      onError={(e) => { e.currentTarget.style.display = "none"; }}
      className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${ok ? "opacity-100" : "opacity-0"} ${className}`}
    />
  );
};

const MAX_PREVIEWS = 3;
let activePreviews = 0;
const previewsAllowed = () => !navigator.connection?.saveData && !window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const LivePreview = ({ id, cardRef }) => {
  const [on, setOn] = useState(false);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const el = cardRef.current;
    if (!el || !previewsAllowed()) return;
    let held = false;
    const want = (yes) => {
      if (yes && !held && activePreviews < MAX_PREVIEWS) { held = true; activePreviews += 1; setOn(true); }
      if (!yes && held) { held = false; activePreviews -= 1; setOn(false); setPlaying(false); }
    };
    const touch = window.matchMedia("(hover: none)").matches;
    const io = touch ? new IntersectionObserver(([e]) => want(e.intersectionRatio >= 0.75), { threshold: [0, 0.75, 1] }) : null;
    io?.observe(el);
    const enter = () => want(true);
    const leave = () => want(false);
    if (!touch) { el.addEventListener("mouseenter", enter); el.addEventListener("mouseleave", leave); }
    return () => { io?.disconnect(); el.removeEventListener("mouseenter", enter); el.removeEventListener("mouseleave", leave); want(false); };
  }, [cardRef]);

  if (!on) return null;
  return (
    <video
      data-testid={`preview-${id}`}
      src={streamUrl(id, localStorage.getItem(KID_KEY))}
      muted
      autoPlay
      loop
      playsInline
      preload="auto"
      disablePictureInPicture
      onPlaying={() => setPlaying(true)}
      className={`absolute inset-0 w-full h-full object-cover pointer-events-none transition-opacity duration-500 ${playing ? "opacity-100" : "opacity-0"}`}
    />
  );
};

export const VideoCard = ({ video, index = 0 }) => {
  const cat = catById(video.category);
  const Icon = cat.icon;
  const cardRef = useRef(null);
  return (
    <Link
      ref={cardRef}
      to={`/kid/watch/${video.id}`}
      data-testid={`video-card-${video.id}`}
      className="zn-card zn-pop group block overflow-hidden zn-lift !rounded-2xl sm:!rounded-3xl"
      style={{ animationDelay: `${Math.min(index, 8) * 60}ms` }}
    >
      <div className="relative aspect-[4/5] sm:aspect-video border-b-[3px] border-[#1B1A2E] zn-dots overflow-hidden" style={{ backgroundColor: cat.color }}>
        <Icon className="absolute -right-4 -bottom-4 opacity-30 rotate-12 w-24 h-24 sm:w-[120px] sm:h-[120px]" strokeWidth={2} />
        {video.thumb && <Thumb id={video.id} title={video.title} />}
        <LivePreview id={video.id} cardRef={cardRef} />
        <span className="absolute inset-0 grid place-items-center">
          <span className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-white border-[3px] border-[#1B1A2E] grid place-items-center shadow-[3px_3px_0_0_#1B1A2E] group-hover:scale-110 transition-transform">
            <Play fill="#1B1A2E" className="w-5 h-5 sm:w-[26px] sm:h-[26px]" />
          </span>
        </span>
        <span className="absolute top-2 left-2 sm:top-3 sm:left-3 zn-chip !py-1 !px-2 sm:!px-3 bg-white text-xs !border-2"><Icon size={14} /> <span className="hidden sm:inline">{cat.label}</span></span>
        <span className="absolute top-2 right-2 sm:top-3 sm:right-3 zn-chip !py-1 !px-2 sm:!px-2.5 bg-[#1B1A2E] text-white text-xs !border-2">{Math.round(video.duration || 10)}s</span>
      </div>
      <div className="p-3 sm:p-4 flex gap-2.5 sm:gap-3 items-start">
        <KidAvatar avatar={video.author?.avatar} color={video.author?.color} frame={video.author?.frame} size={40} className="hidden sm:inline-grid" />
        <div className="min-w-0 flex-1">
          <h3 className="font-display font-semibold text-base sm:text-lg leading-tight truncate">{video.title}</h3>
          <p className="text-xs sm:text-sm text-[#1B1A2E]/60 flex items-center gap-1.5 sm:gap-2 min-w-0">
            <span className="truncate">{video.author?.name}<span className="hidden sm:inline"> · {timeAgo(video.approved_at || video.created_at)}</span></span>
            <span className="inline-flex items-center gap-1 ml-auto shrink-0"><Heart size={13} /> {video.cheers?.length || 0}</span>
          </p>
        </div>
      </div>
    </Link>
  );
};
